import React, { useContext, useEffect, useState } from 'react';
import './Users.scss';
import { observer } from 'mobx-react';
import { rootContext } from 'stores';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Table,
  Popconfirm,
  Checkbox
} from 'antd';
import { PageHeader } from '@ant-design/pro-components';
import {
  EditFilled,
  SearchOutlined,
  QuestionCircleOutlined,
  DeleteFilled
} from '@ant-design/icons';
import { IUsersRegion, pagination, sortByParams } from 'types';
import { SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';
import { sortOrderType } from 'common/utils';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import AddUserRegion from 'components/Craeteuser/AddUserRegion';
import { useDebounceState } from 'hooks/useDebounce.hook';

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 16 }
};
const UserRegions: React.FC = observer(() => {
  const {
    UsersRegions: {
      state,
      state: { data, ui },
      setState,
      getUserRegions,
      editUserRegions,
      deleteRegion
    },
    AuthStore: { isAdmin, isDistributor, isEmployee }
  } = useContext(rootContext);

  const [editForm] = Form.useForm();

  const [
    editUserRegionData,
    setEditUserRegionData
  ] = useState<null | IUsersRegion>(null);
  const [addUserModal, setAddUserModal] = useState<boolean>(false);
  const [search, deSearch, setSearch] = useDebounceState('', 500);
  const [isShippingAvailable, setIsShippingAvailable] = useState(false);

  const tableOnChange = (
    pagination: TablePaginationConfig,
    _: any,
    sort: any
  ) => {
    sort = sort as SorterResult<IUsersRegion>;
    const sortField = sort.columnKey as string;
    const sortBy = sort.order && sortOrderType(sort.order);
    const limit = pagination.pageSize || data.getUserRegionsParams.limit;
    const offset = ((pagination?.current || 1) - 1) * limit;

    let getUserRegionsParams: sortByParams & pagination = {
      limit,
      offset
    };

    if (sortField && sortBy) {
      getUserRegionsParams = { ...getUserRegionsParams, sortBy, sortField };
    }

    setState({
      data: {
        ...state.data,
        getUserRegionsParams
      }
    });
  };

  useEffect(() => {
    getUserRegions({ ...data.getUserRegionsParams, search: deSearch });
  }, [getUserRegions, data.getUserRegionsParams, deSearch]);

  const navigate = useNavigate();
  return (
    <div className="UsersStyles PartsStyles">
      <AddUserRegion
        addUserModal={addUserModal}
        setAddUserModal={setAddUserModal}
      />
      <div className="cardWrapeer">
        <Card>
          <PageHeader
            title="Country Origin"
            onBack={() => navigate(-1)}
            extra={
              <div>
                <Input
                  className="search"
                  placeholder="Search"
                  addonBefore={<SearchOutlined />}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {isAdmin || isDistributor || isEmployee ? (
                  <Button onClick={() => setAddUserModal(true)}>
                    Add country origin
                  </Button>
                ) : (
                  ''
                )}
              </div>
            }
          />
          <Table<IUsersRegion>
            rowKey="id"
            className="Table_block_main"
            loading={ui.isUserRegionsLoading}
            dataSource={data.userRegions}
            pagination={{
              defaultCurrent: 1,
              defaultPageSize: data.getUserRegionsParams.limit,
              total: data.total || 0,
              showSizeChanger: true,
              pageSizeOptions: ['10', '15', '20']
            }}
            onChange={(pagination, _, sort) =>
              tableOnChange(pagination, _, sort)
            }
            columns={[
              {
                dataIndex: 'country_name',
                title: 'Country Name',
                align: 'center',
                key: 'country_name',
                sorter: true
              },
              {
                dataIndex: 'country_code',
                title: 'Country Code',
                align: 'center',
                key: 'country_code',
                sorter: true
              },
              {
                dataIndex: 'createdAt',
                title: 'Created At',
                key: 'createdAt',
                sorter: true,
                render: value => dayjs(value).format('DD/MM/YYYY HH:mm')
              },

              {
                key: 'Actions',
                title: 'Actions',
                render: (value, record: IUsersRegion) => (
                  <div className="actionButtons">
                    <Button
                      type="default"
                      onClick={e => {
                        e.stopPropagation();
                        editForm.setFieldsValue({
                          name: record.country_name,
                          code: record.country_code
                        });
                        setEditUserRegionData(record);
                      }}
                    >
                      <EditFilled />
                      Edit
                    </Button>
                    <Popconfirm
                      title="Are you sure？"
                      icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                      onConfirm={async () => {
                        await deleteRegion(record.id);
                        getUserRegions({
                          ...data.getUserRegionsParams,
                          search: deSearch
                        });
                      }}
                    >
                      <Button
                        type="default"
                        className="deleteButton"
                        disabled={ui.deleteUserLoading}
                      >
                        <DeleteFilled />
                      </Button>
                    </Popconfirm>
                  </div>
                )
              }
            ]}
          ></Table>
          <Modal
            visible={editUserRegionData ? true : false}
            title={`Edit ${editUserRegionData?.country_name}`}
            onCancel={() => {
              setEditUserRegionData(null);
              editForm.resetFields();
            }}
            onOk={editForm.submit}
            okButtonProps={{
              loading: editUserRegionData?.id === ui.editUserLoading
            }}
            destroyOnClose={true}
          >
            <Form
              {...layout}
              form={editForm}
              onFinish={async values => {
                console.log(values);
                editUserRegionData?.id &&
                  (await editUserRegions({
                    id: editUserRegionData.id,
                    name: values.name,
                    code: values.code,
                    is_shipping_available: isShippingAvailable
                  }));
                setEditUserRegionData(null);
                getUserRegions({
                  ...data.getUserRegionsParams,
                  search: deSearch
                });
              }}
            >
              <Form.Item
                label="Country Name"
                name="name"
                normalize={e => (/\s/.test(e) ? e.slice(0, -1) : e)}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Please enter name!'
                  }
                ]}
              >
                <Input
                  name="country_name"
                  placeholder="Enter country name"
                  autoComplete="off"
                />
              </Form.Item>
              <Form.Item
                label="Country Code"
                name="code"
                normalize={e => (/\s/.test(e) ? e.slice(0, -1) : e)}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Please enter code!'
                  }
                ]}
              >
                <Input
                  name="country_code"
                  placeholder="Enter country code like US etc."
                  autoComplete="off"
                  maxLength={2}
                />
              </Form.Item>
              <Form.Item
                label="Shipping Available"
                tooltip="All regions will be considered."
                name="isShippingAvailables"
              >
                <Checkbox
                  defaultChecked={editUserRegionData?.is_shipping_available}
                  style={{ marginLeft: '10px' }}
                  onChange={e => setIsShippingAvailable(e.target.checked)}
                ></Checkbox>
              </Form.Item>
            </Form>
          </Modal>
        </Card>
      </div>
    </div>
  );
});

export default UserRegions;
